import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { getStoredPageSize, setStoredPageSize } from '@/components/Pagination';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { AppShell3 } from '@/components/app-shell-3';
import { ToolHeader } from '@/components/ToolHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Eye,
  Edit,
  Copy,
  Trash2,
  Globe,
  Mail,
  Building2,
  MoreHorizontal,
  Phone,
  AlertTriangle,
  Download,
} from 'lucide-react';
import { GLOBAL_TOOLS } from '@/config/tools';
import { Table, TableCard } from '@/components/application/table/table-fixed';
import { PageTransition } from '@/components/PageTransition';

// Generate skeleton placeholder items for loading state
const skeletonItems = Array.from({ length: 5 }, (_, i) => ({
  id: `skeleton-${i}`,
  trainer_name: '',
  website: '',
  contact_number: '',
  booking_email: '',
  delivery_methods: '',
  isLoading: true,
}));

export function TrainersPage() {
  const navigate = useNavigate();
  const { userProfile, company } = useAuth();
  usePageTitle('Training Providers');
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingTrainer, setEditingTrainer] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => getStoredPageSize(10));

  // Form state for add/edit
  const [formData, setFormData] = useState({
    trainer_name: '',
    website: '',
    contact_number: '',
    booking_email: '',
    delivery_methods: ''
  });

  // Global tools for company-wide navigation (from central config)
  const globalTools = GLOBAL_TOOLS;

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceTimerRef = useRef(null);

  // Debounce search input
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  // Fetch trainers on mount and set up real-time subscription
  useEffect(() => {
    if (company?.id) {
      fetchTrainers();

      // Set up real-time subscription for both global and company trainers
      const subscription = supabase
        .channel(`trainers-${company.id}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'trainers'
          },
          payload => {
            const isRelevant =
              payload.eventType === 'DELETE'
                ? true
                : !payload.new.company_id || payload.new.company_id === company.id;

            if (!isRelevant) return;

            if (payload.eventType === 'INSERT') {
              setTrainers(prev => {
                const exists = prev.some(t => t.id === payload.new.id);
                if (exists) return prev;
                return [...prev, payload.new].sort(
                  (a, b) => (a.display_order || 999) - (b.display_order || 999)
                );
              });
            } else if (payload.eventType === 'UPDATE') {
              setTrainers(prev =>
                prev.map(t => (t.id === payload.new.id ? payload.new : t))
              );
            } else if (payload.eventType === 'DELETE') {
              setTrainers(prev => prev.filter(t => t.id !== payload.old.id));
            }
          }
        )
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [company?.id]);

  const fetchTrainers = async () => {
    if (!company?.id) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('trainers')
        .select('*')
        .or(`company_id.is.null,company_id.eq.${company.id}`)
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) throw error;

      setTrainers(data || []);
    } catch (error) {
      console.error('Error fetching trainers:', error);
      toast.error('Failed to load trainers');
    } finally {
      setLoading(false);
    }
  };

  // Filter trainers based on debounced search query
  const filteredTrainers = useMemo(() => {
    if (!debouncedSearch) return trainers;

    const query = debouncedSearch.toLowerCase();
    return trainers.filter(
      trainer =>
        trainer.trainer_name?.toLowerCase().includes(query) ||
        trainer.booking_email?.toLowerCase().includes(query) ||
        trainer.delivery_methods?.toLowerCase().includes(query)
    );
  }, [trainers, debouncedSearch]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredTrainers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTrainers = filteredTrainers.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  const handleView = useCallback((trainer) => {
    navigate(`/trainers/${trainer.id}`);
  }, [navigate]);

  const handleEdit = useCallback((trainer) => {
    if (!trainer.company_id) {
      toast.error('Cannot edit global training providers');
      return;
    }
    setEditingTrainer(trainer);
    setFormData({
      trainer_name: trainer.trainer_name || '',
      website: trainer.website || '',
      contact_number: trainer.contact_number || '',
      booking_email: trainer.booking_email || '',
      delivery_methods: trainer.delivery_methods || ''
    });
    setShowAddDialog(true);
  }, []);

  const handleDuplicate = useCallback(async (trainer) => {
    if (!trainer.company_id) {
      toast.error('Cannot duplicate global training providers');
      return;
    }

    if (!company?.id) {
      toast.error('No company selected');
      return;
    }

    try {
      const maxOrder = Math.max(...trainers.map(t => t.display_order || 999), 1);

      const duplicatedTrainer = {
        company_id: company.id,
        trainer_name: `${trainer.trainer_name} (Copy)`,
        website: trainer.website,
        contact_number: trainer.contact_number,
        booking_email: trainer.booking_email,
        delivery_methods: trainer.delivery_methods,
        created_by: userProfile?.id,
        display_order: maxOrder + 1
      };

      const { data: newTrainerData, error } = await supabase
        .from('trainers')
        .insert([duplicatedTrainer])
        .select()
        .single();

      if (error) throw error;

      setTrainers(prev => [...prev, newTrainerData].sort(
        (a, b) => (a.display_order || 999) - (b.display_order || 999)
      ));
      toast.success('Trainer duplicated successfully');
    } catch (error) {
      console.error('Error duplicating trainer:', error);
      toast.error('Failed to duplicate trainer');
    }
  }, [company?.id, trainers, userProfile?.id]);

  const handleDeleteClick = useCallback((trainer) => {
    if (!trainer.company_id) {
      toast.error('Cannot delete global training providers');
      return;
    }
    setDeleteTarget(trainer);
    setShowDeleteDialog(true);
  }, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;

    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('trainers')
        .delete()
        .eq('id', deleteTarget.id);

      if (error) throw error;

      setTrainers(prev => prev.filter(t => t.id !== deleteTarget.id));
      toast.success('Trainer deleted successfully');
    } catch (error) {
      console.error('Error deleting trainer:', error);
      toast.error('Failed to delete trainer');
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
      setDeleteTarget(null);
    }
  };

  const handleAddNew = useCallback(() => {
    setEditingTrainer(null);
    setFormData({
      trainer_name: '',
      website: '',
      contact_number: '',
      booking_email: '',
      delivery_methods: ''
    });
    setShowAddDialog(true);
  }, []);

  const handleSave = async () => {
    if (!formData.trainer_name) {
      toast.error('Please enter a trainer name');
      return;
    }

    if (!company?.id) {
      toast.error('No company selected');
      return;
    }

    setIsSaving(true);
    try {
      if (editingTrainer) {
        // Update existing trainer
        const { error } = await supabase
          .from('trainers')
          .update({
            trainer_name: formData.trainer_name,
            website: formData.website,
            contact_number: formData.contact_number,
            booking_email: formData.booking_email,
            delivery_methods: formData.delivery_methods
          })
          .eq('id', editingTrainer.id);

        if (error) throw error;

        setTrainers(prev => prev.map(t =>
          t.id === editingTrainer.id ? { ...t, ...formData } : t
        ));
        toast.success('Trainer updated successfully');
      } else {
        // Add new trainer
        const maxOrder = Math.max(...trainers.map(t => t.display_order || 999), 1);

        const trainerData = {
          company_id: company.id,
          trainer_name: formData.trainer_name,
          website: formData.website,
          contact_number: formData.contact_number,
          booking_email: formData.booking_email,
          delivery_methods: formData.delivery_methods,
          created_by: userProfile?.id,
          display_order: maxOrder + 1
        };

        const { data: savedTrainer, error } = await supabase
          .from('trainers')
          .insert([trainerData])
          .select()
          .single();

        if (error) throw error;

        setTrainers(prev => [...prev, savedTrainer].sort(
          (a, b) => (a.display_order || 999) - (b.display_order || 999)
        ));
        toast.success('Trainer added successfully');
      }

      setShowAddDialog(false);
      setFormData({
        trainer_name: '',
        website: '',
        contact_number: '',
        booking_email: '',
        delivery_methods: ''
      });
      setEditingTrainer(null);
    } catch (error) {
      console.error('Error saving trainer:', error);
      toast.error('Failed to save trainer');
    } finally {
      setIsSaving(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <AppShell3>
      <PageTransition>
        <ToolHeader
          spaceName="Company"
          toolName="Trainers"
          currentTool="trainers"
          spaceTools={globalTools}
          isGlobalView={true}
          actions={
            <div className='flex items-center gap-3'>
              <div className='relative'>
                <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400' />
                <Input
                  type='text'
                  placeholder='Search providers...'
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className='pl-10 bg-white w-64'
                />
              </div>
              <Button
                size='sm'
                onClick={handleAddNew}
              >
                <Plus className='h-4 w-4 mr-2' />
                Add Provider
              </Button>
            </div>
          }
        />

        {/* Main Content Container */}
        <div className='max-w-7xl mx-auto mt-4'>
          {/* Stats Cards */}
          <div className='grid grid-cols-3 gap-4 mb-6'>
            <div className='bg-white p-4 rounded-lg border'>
              <div className='flex items-center justify-between'>
                <div>
                  <p className='text-sm text-gray-500'>Total Providers</p>
                  <p className='text-2xl font-bold'>{trainers.length}</p>
                </div>
                <Building2 className='h-8 w-8 text-gray-400' />
              </div>
            </div>
            <div className='bg-white p-4 rounded-lg border'>
              <div className='flex items-center justify-between'>
                <div>
                  <p className='text-sm text-gray-500'>Verified</p>
                  <p className='text-2xl font-bold text-blue-600'>
                    {trainers.filter(t => !t.company_id).length}
                  </p>
                </div>
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  Global
                </Badge>
              </div>
            </div>
            <div className='bg-white p-4 rounded-lg border'>
              <div className='flex items-center justify-between'>
                <div>
                  <p className='text-sm text-gray-500'>Custom</p>
                  <p className='text-2xl font-bold text-green-600'>
                    {trainers.filter(t => t.company_id).length}
                  </p>
                </div>
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  Company
                </Badge>
              </div>
            </div>
          </div>

          {/* Trainers Table */}
          <TableCard.Root>
            <TableCard.Header
              title="Training providers"
              badge={`${filteredTrainers.length} providers`}
            />
            <Table aria-label="Training providers">
              <Table.Header>
                <Table.Head id="name" label="Provider" isRowHeader className="w-[28%]" />
                <Table.Head id="website" label="Website" className="w-[12%]" />
                <Table.Head id="phone" label="Phone" className="w-[14%]" />
                <Table.Head id="email" label="Booking Email" className="w-[20%]" />
                <Table.Head id="delivery" label="Delivery Methods" className="w-[18%]" />
                <Table.Head id="actions" label="" className="w-[8%]" />
              </Table.Header>

              <Table.Body items={loading ? skeletonItems : paginatedTrainers}>
                {(trainer) => (
                  <Table.Row id={trainer.id}>
                    <Table.Cell>
                      {trainer.isLoading ? (
                        <div className="flex items-center gap-2">
                          <Skeleton className="h-8 w-8 rounded-full" />
                          <Skeleton className="h-4 w-28" />
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 min-w-0">
                          <Avatar className="h-8 w-8 flex-shrink-0 bg-gray-100">
                            <AvatarFallback className="text-xs bg-gray-100 text-gray-600">
                              {getInitials(trainer.trainer_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <p className="text-sm font-medium text-gray-900 truncate" title={trainer.trainer_name}>
                                {trainer.trainer_name}
                              </p>
                              {!trainer.company_id && (
                                <Badge
                                  variant="outline"
                                  className="text-[9px] px-1 py-0 flex-shrink-0 bg-blue-50 text-blue-700 border-blue-200"
                                >
                                  Verified
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {trainer.isLoading ? (
                        <Skeleton className="h-4 w-20" />
                      ) : trainer.website ? (
                        <a
                          href={trainer.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline text-sm truncate block"
                          title={trainer.website}
                        >
                          {trainer.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                        </a>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {trainer.isLoading ? (
                        <Skeleton className="h-4 w-20" />
                      ) : (
                        <span className="text-sm truncate block" title={trainer.contact_number || '-'}>
                          {trainer.contact_number || '-'}
                        </span>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {trainer.isLoading ? (
                        <Skeleton className="h-4 w-32" />
                      ) : (
                        <div className="flex items-center gap-1.5 min-w-0">
                          {trainer.booking_email ? (
                            <>
                              <Mail className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                              <span className="text-sm truncate" title={trainer.booking_email}>
                                {trainer.booking_email}
                              </span>
                            </>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </div>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {trainer.isLoading ? (
                        <div className="flex gap-1">
                          <Skeleton className="h-5 w-12 rounded" />
                          <Skeleton className="h-5 w-12 rounded" />
                        </div>
                      ) : trainer.delivery_methods ? (
                        <div className="flex flex-wrap gap-1">
                          {trainer.delivery_methods.split(',').slice(0, 2).map((method, i) => (
                            <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0">
                              {method.trim()}
                            </Badge>
                          ))}
                          {trainer.delivery_methods.split(',').length > 2 && (
                            <span className="text-[10px] text-gray-500">
                              +{trainer.delivery_methods.split(',').length - 2}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {trainer.isLoading ? (
                        <div className="flex justify-end">
                          <Skeleton className="h-8 w-8 rounded" />
                        </div>
                      ) : (
                        <div className="flex justify-end">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleView(trainer)}>
                                <Eye className="h-4 w-4 mr-2" />
                                View Profile
                              </DropdownMenuItem>
                              {trainer.company_id && (
                                <>
                                  <DropdownMenuItem onClick={() => handleEdit(trainer)}>
                                    <Edit className="h-4 w-4 mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleDuplicate(trainer)}>
                                    <Copy className="h-4 w-4 mr-2" />
                                    Duplicate
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => handleDeleteClick(trainer)}
                                    className="text-red-600 focus:text-red-600"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      )}
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>

            {/* Pagination */}
            <div className="px-4 py-3 md:px-6 md:pt-3 md:pb-4 border-t">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-700">
                  Showing <span className="font-medium">{filteredTrainers.length > 0 ? startIndex + 1 : 0}</span> to{' '}
                  <span className="font-medium">{Math.min(endIndex, filteredTrainers.length)}</span> of{' '}
                  <span className="font-medium">{filteredTrainers.length}</span> results
                </span>

                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-700">Per page:</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        setItemsPerPage(val);
                        setCurrentPage(1);
                        setStoredPageSize(val);
                      }}
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    >
                      First
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(page => {
                          return (
                            page === 1 ||
                            page === totalPages ||
                            (page >= currentPage - 1 && page <= currentPage + 1)
                          );
                        })
                        .map((page, index, array) => {
                          const prevPage = array[index - 1];
                          const showEllipsis = prevPage && page - prevPage > 1;

                          return (
                            <span key={page} className="contents">
                              {showEllipsis && (
                                <span className="px-2 text-gray-500">...</span>
                              )}
                              <Button
                                variant={currentPage === page ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setCurrentPage(page)}
                                className="w-9"
                              >
                                {page}
                              </Button>
                            </span>
                          );
                        })}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages || totalPages === 0}
                    >
                      Next
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages || totalPages === 0}
                    >
                      Last
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </TableCard.Root>
        </div>

        {/* Add/Edit Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>
                {editingTrainer ? 'Edit Training Provider' : 'Add Training Provider'}
              </DialogTitle>
              <DialogDescription>
                {editingTrainer
                  ? 'Update the details for this training provider.'
                  : 'Add a new training provider to your organization.'}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="trainer_name">Provider Name *</Label>
                <Input
                  id="trainer_name"
                  value={formData.trainer_name}
                  onChange={(e) => setFormData({ ...formData, trainer_name: e.target.value })}
                  placeholder="Enter provider name"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://example.com"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="contact_number">Phone Number</Label>
                  <Input
                    id="contact_number"
                    value={formData.contact_number}
                    onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })}
                    placeholder="Phone number"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="booking_email">Booking Email</Label>
                  <Input
                    id="booking_email"
                    type="email"
                    value={formData.booking_email}
                    onChange={(e) => setFormData({ ...formData, booking_email: e.target.value })}
                    placeholder="booking@example.com"
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="delivery_methods">Delivery Methods</Label>
                <Input
                  id="delivery_methods"
                  value={formData.delivery_methods}
                  onChange={(e) => setFormData({ ...formData, delivery_methods: e.target.value })}
                  placeholder="e.g., In-person, Online, Blended"
                />
                <p className="text-xs text-gray-500">Separate multiple methods with commas</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : editingTrainer ? 'Save Changes' : 'Add Provider'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent className="p-0 overflow-hidden">
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-100">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div className="flex-1 space-y-1">
                  <AlertDialogTitle className="text-lg font-semibold">Delete Training Provider</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone.
                  </AlertDialogDescription>
                </div>
              </div>
              <div className="rounded-lg bg-muted p-4">
                <p className="text-sm text-muted-foreground">
                  Are you sure you want to delete <span className="font-medium text-foreground">"{deleteTarget?.trainer_name}"</span>?
                </p>
              </div>
              <AlertDialogFooter className="gap-3 pt-2">
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete Provider'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </div>
          </AlertDialogContent>
        </AlertDialog>
      </PageTransition>
    </AppShell3>
  );
}
